import React, { useState, useRef, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import ReactDOM from 'react-dom';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Multi-select dropdown — same design as the chip-style picker used by
 * AddInitiativeScreen for "الأقسام المتعاونة". Self-contained, web-only
 * (uses a fixed-position portal for the menu).
 */

export interface MultiSelectDropdownOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  values: string[];
  options: MultiSelectDropdownOption[];
  placeholder: string;
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function MultiSelectDropdown({
  values,
  options,
  placeholder,
  onChange,
  disabled,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();

  const selectedOptions = options.filter(opt => values.includes(opt.value));

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : '#F1F5F9',
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const toggleOption = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onChange(values.filter(v => v !== optionValue));
    } else {
      onChange([...values, optionValue]);
    }
  };

  const removeOption = (optionValue: string) => {
    onChange(values.filter(v => v !== optionValue));
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef as any}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        left: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.map((option) => {
            const isSelected = values.includes(option.value);
            return (
              <TouchableOpacity
                key={option.value}
                style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
                onPress={() => toggleOption(option.value)}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <View style={{
                    width: 20, height: 20, borderRadius: 4,
                    borderWidth: 2, borderColor: isSelected ? '#A9211F' : '#D1D5DB',
                    backgroundColor: isSelected ? '#A9211F' : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && (
                      <Svg width={12} height={12} viewBox="0 0 24 24">
                        <Path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </Svg>
                    )}
                  </View>
                  <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, flex: 1 }]}>{option.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg }
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        {selectedOptions.length === 0 ? (
          <Text style={[styles.dropdownButtonText, { color: dropdownTheme.placeholder }]}>
            {placeholder}
          </Text>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, flex: 1, justifyContent: 'flex-end' }}>
            {selectedOptions.map(opt => (
              <View key={opt.value} style={{
                flexDirection: 'row', alignItems: 'center', gap: 4,
                backgroundColor: isDark ? '#374151' : '#F1F5F9',
                borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
              }}>
                <TouchableOpacity onPress={() => removeOption(opt.value)} hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                  <Text style={{ color: '#EF4444', fontSize: 14, fontFamily: FONTS.regular }}>×</Text>
                </TouchableOpacity>
                <Text style={{ color: dropdownTheme.text, fontSize: 13, fontFamily: FONTS.regular }}>{opt.label}</Text>
              </View>
            ))}
          </View>
        )}
      </TouchableOpacity>
      {portalContent}
    </View>
  );
}

const styles = StyleSheet.create({
  dropdownContainer: {
    position: 'relative',
    width: '100%',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    cursor: 'pointer' as any,
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
});
